import * as React from "react"
import { DropdownType } from "@/types"
import { useQueries } from "@tanstack/react-query"
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { Eye } from "lucide-react"

import { getConnectionType } from "@/lib/api/billing"
import { cn } from "@/lib/utils"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover-modal"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Icons } from "@/components/icons"
import { Loader } from "@/components/loader"

import { ZoneBookFilter } from "./zone-book-filter"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onSelectedRowsChange?: (rows: TData[]) => void
  children?: React.ReactNode
  search?: {
    field?: string
    label: string
    value?: string
    onChange?: React.Dispatch<React.SetStateAction<string>>
  }
  defaultPage?: number
  showSelectedRows?: boolean
  isLoading?: boolean
  isError?: boolean
  setDefaultPage?: React.Dispatch<React.SetStateAction<number>>
  nextPageUrl?: string | null
  prevPageUrl?: string | null
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  totalCount?: number

  zones?: number[]
  setZones?: React.Dispatch<React.SetStateAction<number[]>>

  books?: number[]
  setBooks?: React.Dispatch<React.SetStateAction<number[]>>

  connectionTypes?: number[]
  setConnectionTypes?: React.Dispatch<React.SetStateAction<number[]>>

  inCard?: boolean
}

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  children,
  search,
  defaultPage,
  showSelectedRows = false,
  isLoading,
  isError,
  setDefaultPage,
  nextPageUrl,
  prevPageUrl,
  page,
  setPage,
  totalCount,
  onSelectedRowsChange,

  books,
  setBooks,

  zones,
  setZones,

  connectionTypes,
  setConnectionTypes = () => {},
  inCard,
}: DataTableProps<TData, TValue>) {
  const isDesktop = useMediaQuery("(min-width: 1367px)")
  const isLaptop = useMediaQuery("(min-width: 768px)")
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  )

  const isConnectionTypeDropdown = true

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: defaultPage || 10,
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  React.useEffect(() => {
    onSelectedRowsChange?.(
      table.getFilteredSelectedRowModel().rows.map((row) => row.original),
    )
  }, [table.getFilteredSelectedRowModel().rows, onSelectedRowsChange])

  function getPageFromUrl(url: string): number {
    const params = new URLSearchParams(new URL(url).search)
    console.log(url)

    return Number(params.get("page")) || 1
  }

  /*if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader />
      </div>
    )
  }*/

  if (isError) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-destructive">
          Error loading data. Please try again.
        </p>
      </div>
    )
  }

  const pageCount =
    totalCount && defaultPage
      ? Math.ceil(totalCount / defaultPage)
      : table.getPageCount()

  const currentPage = page ?? table.getState().pagination.pageIndex

  const queryConfigs = [
    {
      queryKey: ["connectionType"],
      queryFn: () => getConnectionType(isConnectionTypeDropdown),
    },
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  const [connectionTypeData] = results.map((result) => result.data || [])

  const handleConnectionTypeCheckboxChange = (id: number) => {
    setConnectionTypes((prev) =>
      prev.includes(id)
        ? prev.filter((zoneId) => zoneId !== id)
        : [...prev, id],
    )
  }

  return (
    <div className="flex flex-col items-center">
      <div className="mb-4 flex w-full flex-wrap gap-2 pt-1 md:flex-nowrap">
        <div className="flex items-center justify-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Show {table.getState().pagination.pageSize} rows{" "}
                <Icons.down className="mr-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {[10, 25, 50, 100].map((pageSize) => (
                <DropdownMenuItem
                  key={pageSize}
                  onSelect={() => {
                    table.setPageSize(pageSize)
                    setPage?.(1)
                    setDefaultPage?.(pageSize)
                  }}
                >
                  Show {pageSize}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {showSelectedRows && (
            <div className="w-36 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} row(s) selected
            </div>
          )}
        </div>
        {search && (
          <Input
            placeholder={`Search ${search.label}...`}
            /*    value={
              search.onChange && search.value
                ? search.value
                : search.field
                  ? (table.getColumn(search.field)?.getFilterValue() as string)
                  : ""
            } */
            value={
              search.value ??
              (search.field
                ? (table.getColumn(search.field)?.getFilterValue() as string)
                : "")
            }
            onChange={(event) => {
              const newValue = event.target.value

              if (search.onChange) {
                setPage?.(1)
                search.onChange(newValue)
              }

              if (search.field) {
                table.getColumn(search.field)?.setFilterValue(newValue)
              }
            }}
            className="max-w-sm"
          />
        )}

        {children ? <>{children}</> : null}
        <Popover>
          <PopoverTrigger>
            <Button variant="outline" className="md:ml-auto">
              <Icons.mixer className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-full dark:bg-[#F7F7F7] dark:text-gray-900"
          >
            <div className="my-2">
              <h1 className="text-xl font-semibold">Filter</h1>
            </div>
            <DropdownMenuSeparator className="my-2 bg-gray-300 px-2" />
            <Card className="border-gray-300 dark:bg-[#FFFFFF] dark:text-gray-900">
              <CardHeader className="flex flex-row gap-8 px-2 py-0">
                <Button className="bg-tranparent hover:bg-tranparent mt-1 flex text-gray-900">
                  <Eye height="15px" width="15px" />
                  <span>Show counts</span>
                </Button>

                <Button className="bg-tranparent hover:bg-tranparent text-destructive">
                  <Icons.x height="15px" width="15px" />
                  <span className="text-nowrap">Clear All Filters</span>
                </Button>
              </CardHeader>

              <CardContent>
                <DropdownMenuSeparator className="bg-gray-300 px-2" />
                <div className="my-1 flex items-start gap-2">
                  <Input
                    type="checkbox"
                    checked={
                      table
                        .getAllColumns()
                        .some((column) => !column.getIsVisible())
                        ? false
                        : true
                    }
                    onChange={(e) =>
                      table
                        .getAllColumns()
                        .forEach((column) =>
                          column.toggleVisibility(e.target.checked),
                        )
                    }
                    className="h-4 w-4"
                  />
                  <span className="text-nowrap text-[14px]">All</span>
                </div>
                {table.getAllColumns().map((column, index) => (
                  <div key={index} className="my-1 flex items-start gap-2">
                    <Input
                      type="checkbox"
                      checked={column.getIsVisible()}
                      onChange={(e) =>
                        column.toggleVisibility(e.target.checked)
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-nowrap text-[14px]">
                      {column.id.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </CardContent>

              {connectionTypes && (
                <CardContent>
                  <span className="font-semibold">Connection Types</span>
                  <DropdownMenuSeparator className="bg-gray-300 px-2" />
                  <div className="my-1 flex flex-col gap-2">
                    {connectionTypeData.map((connectionType: DropdownType) => {
                      return (
                        <div key={connectionType.id} className="flex flex-col">
                          <label className="flex items-center space-x-2">
                            <Input
                              type="checkbox"
                              value={connectionType.id}
                              checked={connectionTypes.includes(
                                connectionType.id,
                              )}
                              onChange={() =>
                                handleConnectionTypeCheckboxChange(
                                  connectionType.id,
                                )
                              }
                              className="h-4 w-4"
                            />
                            <span
                              onClick={() =>
                                handleConnectionTypeCheckboxChange(
                                  connectionType.id,
                                )
                              }
                            >
                              {connectionType.label}
                            </span>
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              )}

              <CardContent>
                <div>
                  <ZoneBookFilter
                    setZones={setZones}
                    zones={zones}
                    setBooks={setBooks}
                    books={books}
                  />
                </div>
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader />
        </div>
      ) : !inCard && (isDesktop || isLaptop) ? ( //!inCard && (isDesktop || (isLaptop && columns.length <= 6)) ? (
        <div className="overflow-hidden rounded-md border border-neutral-300 dark:border-border md:w-[calc(100vw-10rem)] lg:w-full print:w-[1920px]">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-center gap-6 border-t border-neutral-300 py-4 dark:border-border">
            {/* {showSelectedRows && (
              <div className="text-sm text-muted-foreground">
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected
              </div>
            )} */}
            {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Show {table.getState().pagination.pageSize} rows
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {[1, 2, 4, 6, 8].map((pageSize) => (
                  <DropdownMenuItem
                    key={pageSize}
                    onSelect={() => {
                      table.setPageSize(pageSize)
                      setPage?.(1)
                      setDefaultPage?.(pageSize)
                    }}
                  >
                    Show {pageSize}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu> */}

            {/*Pagination*/}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (prevPageUrl) {
                    const prevPage = getPageFromUrl(prevPageUrl)
                    setPage?.(prevPage)
                  }
                  table.previousPage()
                }}
                disabled={!prevPageUrl && !table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <div className="flex items-center space-x-2">
                {Array.from({ length: pageCount }, (_, i) => i)
                  .filter((_page) => {
                    return (
                      _page === 0 ||
                      _page === pageCount - 1 ||
                      (currentPage <= 1 && _page <= 2) ||
                      (currentPage >= pageCount - 2 &&
                        _page >= pageCount - 3) ||
                      (_page >= currentPage - 1 && _page <= currentPage + 1)
                    )
                  })
                  .map((__page, index, arr) => {
                    const prevPage = arr[index - 1]
                    const pageNumber = totalCount ? __page + 1 : __page

                    return (
                      <React.Fragment key={__page}>
                        {prevPage !== undefined && __page - prevPage > 1 && (
                          <span className="text-gray-400">...</span>
                        )}
                        <Button
                          key={__page}
                          variant={
                            pageNumber === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => {
                            setPage?.(pageNumber) || table.setPageIndex(__page)
                          }}
                        >
                          {totalCount ? pageNumber : pageNumber + 1}
                        </Button>
                      </React.Fragment>
                    )
                  })}

                {/* Array.from({ length: table.getPageCount() }, (_, i) => i)
                  .filter((page) => {
                    const currentPage = table.getState().pagination.pageIndex
                    const totalPages = table.getPageCount()

                    //show first & last pages, and dynamically adjust visible range
                    return (
                      page === 0 || // First page
                      page === totalPages - 1 || // Last page
                      (currentPage <= 1 && page <= 2) || // Show 1, 2, 3 when at the start
                      (currentPage >= totalPages - 2 &&
                        page >= totalPages - 3) || // Show last 3 pages when near end
                      (page >= currentPage - 1 && page <= currentPage + 1) // Show surrounding pages
                    )
                  })
                  .map((page, index, arr) => {
                    const prevPage = arr[index - 1]
                    //console.log( Array.from({ length: table.getPageCount() }, (_, i) => i));

                    return (
                      <React.Fragment key={page}>
                        {prevPage !== undefined && page - prevPage > 1 && (
                          <span>...</span>
                        )}
                        <Button
                          key={page}
                          variant={
                            page === table.getState().pagination.pageIndex
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => table.setPageIndex(page)}
                        >
                          {page + 1}
                        </Button>
                      </React.Fragment>
                    )
                  }) */}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (nextPageUrl) {
                    const nextPage = getPageFromUrl(nextPageUrl)

                    console.log(nextPage)

                    setPage?.(nextPage)
                  }
                  table.nextPage()
                }}
                disabled={!nextPageUrl && !table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {table.getRowModel().rows?.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <Card
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className={cn(
                  "overflow-hidden p-4",
                  `${row.getIsSelected() ? "bg-accent" : "bg-background"}`,
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="flex justify-between py-2">
                    <span className="font-medium">
                      {flexRender(
                        cell.column.columnDef.header,
                        cell.getContext() as any,
                      )}
                    </span>
                    <span>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </span>
                  </div>
                ))}
              </Card>
            ))
          ) : (
            <div className="p-4 text-center">
              <p>No results.</p>
            </div>
          )}
        </div>
      )}

      {/*<div className="flex items-center justify-end gap-6 py-4">
        {showSelectedRows && (
          <div className="text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              Show {table.getState().pagination.pageSize} rows
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {[2, 4, 6, 8].map((pageSize) => (
              <DropdownMenuItem
                key={pageSize}
                onSelect={() => {
                  table.setPageSize(pageSize)
                  setPage?.(1)
                  setDefaultPage?.(pageSize)
                }}
              >
                Show {pageSize}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="space-x-2">

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (prevPageUrl) {
                const prevPage = getPageFromUrl(prevPageUrl)
                setPage?.(prevPage)
              }
              table.previousPage()
            }}
            disabled={!prevPageUrl && !table.getCanPreviousPage()}
          >
            Previous
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (nextPageUrl) {
                const nextPage = getPageFromUrl(nextPageUrl)

                console.log(nextPage)

                setPage?.(nextPage)
              }
              table.nextPage()
            }}
            disabled={!nextPageUrl && !table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>*/}
    </div>
  )
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>
  }

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 hover:bg-transparent hover:text-muted focus:bg-transparent data-[state=open]:bg-transparent dark:text-white print:text-xl"
          >
            <span>{title}</span>
            {column.getIsSorted() === "desc" ? (
              <Icons.down className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === "asc" ? (
              <Icons.up className="ml-2 h-4 w-4" />
            ) : (
              <Icons.updown className="ml-2 h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <Icons.up className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <Icons.down className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Desc
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <Icons.updown className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Hide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
